import debuger from 'debug'

import errorTrackingService from 'utils/services/errorTrackingService'

import { DataPreloadManager, PreloadTask, PreloaderRule } from './types'

const debug = debuger('FlowChartDataPreloader')

export class PredicatePreloader {
  private dataPreloadManager: DataPreloadManager

  private getEntityPreloadTaskKey = <InputEntity, Payload = object>({
    entity,
    rule,
    payload,
  }: {
    entity: InputEntity
    rule: PreloaderRule<InputEntity, Payload>
    payload: Payload
  }): string | null => {
    try {
      return rule.getKey(entity, payload)
    } catch (error) {
      errorTrackingService.trackError('Unable to preload entity data: Key generation failed', {
        extra: {
          error: (error as Error)?.message,
          entity,
        },
      })
      return null
    }
  }

  private getEntityPreloadTaskLoader = <InputEntity, Payload = object>({
    entity,
    rule,
    payload,
  }: {
    entity: InputEntity
    rule: PreloaderRule<InputEntity, Payload>
    payload: Payload
  }): (() => Promise<boolean>) | null => {
    try {
      return rule.createLoader(entity, payload)
    } catch (error) {
      errorTrackingService.trackError('Unable to preload entity data: Failed to create loader', {
        extra: {
          error: (error as Error)?.message,
          entity,
        },
      })
      return null
    }
  }

  private getEntityPreloadRules = <InputEntity, Payload = object>({
    entity,
    rules,
    payload,
  }: {
    entity: InputEntity
    rules: Array<PreloaderRule<InputEntity, Payload>>
    payload: Payload
  }): Array<PreloaderRule<InputEntity, Payload>> => {
    return rules.reduce((acc: Array<PreloaderRule<InputEntity, Payload>>, rule) => {
      try {
        if (rule.predicate(entity, payload)) {
          acc.push(rule)
        }
      } catch (error) {
        errorTrackingService.trackError('Unable to preload entity data: Predicate failed', {
          extra: {
            error: (error as Error)?.message,
            entity,
          },
        })
      }

      return acc
    }, [])
  }

  private getEntityMatchingDataPreloadTasks<InputEntity, Payload = object>({
    entity,
    rules,
    payload,
  }: {
    entity: InputEntity
    rules: Array<PreloaderRule<InputEntity, Payload>>
    payload?: Payload
  }): PreloadTask[] {
    const fallbackPayload = {} as unknown as Payload
    const payloadData = payload || fallbackPayload
    const preloadRules = this.getEntityPreloadRules({ entity, rules, payload: payloadData })

    return preloadRules.reduce((acc: PreloadTask[], rule) => {
      const taskKey = this.getEntityPreloadTaskKey({
        entity,
        rule,
        payload: payloadData,
      })
      const loader = this.getEntityPreloadTaskLoader({
        entity,
        rule,
        payload: payloadData,
      })

      if (taskKey && loader) {
        acc.push({
          key: taskKey,
          load: loader,
        })
      }

      return acc
    }, [])
  }

  private getEntityListDataPreloadTasks = <InputEntity, Payload = object>(data: {
    entities: InputEntity[]
    rules: Array<PreloaderRule<InputEntity, Payload>>
    payload?: Payload
  }): PreloadTask[] =>
    Object.values(
      data.entities.reduce((tasksMap: Record<string, PreloadTask>, entity) => {
        const tasksList = this.getEntityMatchingDataPreloadTasks({
          entity,
          rules: data.rules,
          payload: data.payload,
        })

        tasksList.forEach((task) => {
          tasksMap[task.key] = task
        })

        return tasksMap
      }, {}),
    )

  constructor(dataPreloadManager: DataPreloadManager) {
    this.dataPreloadManager = dataPreloadManager
  }

  public preloadDataForEntities<InputEntity, Payload = object>(data: {
    entities: InputEntity[]
    rules: Array<PreloaderRule<InputEntity, Payload>>
    payload?: Payload
  }) {
    const tasks = this.getEntityListDataPreloadTasks(data)

    debug(
      'Preloading data for entities:',
      tasks.map((task) => task.key),
    )

    this.dataPreloadManager.preloadData(tasks)
  }
}
